import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faChartLine,
  faPen,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

/**
 * Step 1 of the learning-path wizard — the path shell (title, description,
 * cover image, price). Mirrors Stage 1 of the course builder: this is the only
 * gated step, and saving it is what unblocks step 2.
 */
const LearningPathDetailsStep = ({
  form,
  setForm,
  isNew,
  hasShell,
  locked,
  status,
  pending,
  showAnalytics,
  analytics,
  fieldClass,
  labelClass,
  primaryBtn,
  ghostBtn,
  onSave,
  onContinue,
  onSubmit,
  onUnsubmit,
  onDelete,
  onToggleAnalytics,
}) => (
  <div className="grid gap-5 lg:grid-cols-[minmax(0,420px)_1fr]">
    <section className="h-fit rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="m-0 text-sm font-bold text-slate-900">1 · Path details</h2>
      <p className="m-0 mt-1 text-xs text-slate-500">
        Name the learning path, then build an ordered chain of courses.
      </p>
      <form className="mt-4 grid gap-3" onSubmit={onSave}>
        <div>
          <label className={labelClass}>Title</label>
          <input
            className={fieldClass}
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            placeholder="Frontend Developer Path"
          />
        </div>
        <div>
          <label className={labelClass}>Description</label>
          <textarea
            className={fieldClass}
            rows={3}
            value={form.description}
            onChange={(e) => setForm({...form, description: e.target.value})}
          />
        </div>
        <div>
          <label className={labelClass}>Cover image URL</label>
          <input
            className={fieldClass}
            value={form.coverImage}
            onChange={(e) => setForm({...form, coverImage: e.target.value})}
            placeholder="https://…"
          />
        </div>
        <div>
          <label className={labelClass}>Price (₦, 0 = free)</label>
          <input
            className={fieldClass}
            type="number"
            min="0"
            value={form.price}
            onChange={(e) => setForm({...form, price: e.target.value})}
          />
        </div>

        {/* Save + Continue. The wizard stays navigable: Continue only unlocks
            once the draft shell exists (hasShell flips when an id arrives). */}
        <div className="mt-2 flex gap-3 border-t border-slate-100 pt-4">
          <button
            type="submit"
            className={`${primaryBtn} flex-1 justify-center`}
            disabled={pending || locked}
          >
            <FontAwesomeIcon icon={faPen} /> {isNew ? "Create draft" : "Save draft"}
          </button>
          <button
            type="button"
            className={`${primaryBtn} flex-1 justify-center`}
            disabled={!hasShell}
            onClick={onContinue}
          >
            Continue to courses <FontAwesomeIcon icon={faArrowDown} />
          </button>
        </div>
        {!hasShell ? (
          <p className="m-0 text-xs text-slate-500">
            Save the draft first — courses attach to an existing learning path.
          </p>
        ) : null}
      </form>

      {!isNew ? (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
          {status === "DRAFT" ? (
            <>
              <button type="button" className={primaryBtn} onClick={onSubmit}>
                Submit for review
              </button>
              <button type="button" className={ghostBtn} onClick={onDelete}>
                <FontAwesomeIcon icon={faTrash} /> Delete
              </button>
            </>
          ) : null}
          {status === "PENDING" ? (
            <button type="button" className={ghostBtn} onClick={onUnsubmit}>
              Withdraw
            </button>
          ) : null}
          <button type="button" className={ghostBtn} onClick={onToggleAnalytics}>
            <FontAwesomeIcon icon={faChartLine} /> Analytics
          </button>
        </div>
      ) : null}
    </section>

    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="m-0 text-sm font-bold text-slate-900">How the bundle works</h2>
      <ol className="m-0 mt-3 grid list-decimal gap-2 pl-5 text-sm text-slate-600">
        <li>Save the learning path details.</li>
        <li>Create or select courses and order them in the path.</li>
        <li>Open each course to build its modules and lessons using the same course builder.</li>
        <li>Return here to manage the course sequence, then submit the path for review.</li>
      </ol>
      {showAnalytics && analytics ? (
        <>
          <h3 className="m-0 mt-5 text-sm font-bold text-slate-900">Analytics</h3>
          <pre className="mt-3 max-h-64 overflow-auto rounded-lg bg-slate-50 p-3 text-[11px] text-slate-700">
            {JSON.stringify(analytics, null, 2)}
          </pre>
        </>
      ) : null}
    </section>
  </div>
);

export default LearningPathDetailsStep;
